import { mdiArrowLeft, mdiChevronRight } from '@mdi/js';
import Icon from '@mdi/react';
import useOnlineStatus from '@rehooks/online-status';
import PrimaryButton from 'Components/Buttons/PrimaryButton';
import SecondaryButton from 'Components/Buttons/SecondaryButton';
import FieldTransition from 'Components/FieldTransition';
import FieldWrapper from 'Components/Forms/FieldWrapper';
import FormWrapper from 'Components/Forms/FormWrapper';
import SelectField from 'Components/Forms/SelectField';
import SwitchField from 'Components/Forms/SwitchField';
import TextareaField from 'Components/Forms/TextareaField';
import TextField from 'Components/Forms/TextField';
import SyncSpinner from 'Components/SyncSpinner';
import { format } from 'date-fns';
import { Field, Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import api from 'Services/api';
import valueStore from 'Services/valueStore';
import useFormikSubmit from 'Support/hooks/useFormikSubmit';
import useOrganization from 'Support/hooks/useOrganization';
import usePageTitle from 'Support/hooks/usePageTitle';
import route from 'Support/route';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

export const entityTypeOptions = [
  {
    value: null,
    label: '---',
  },
  {
    value: 'App\\Models\\Job',
    label: 'Job',
  },
  {
    value: 'App\\Models\\Report',
    label: 'Form',
  },
  {
    value: 'App\\Models\\ViewLog',
    label: 'View Log',
  },
];

export const formatAnchorLabel = (dueAt) => {
  if (!dueAt) return null;
  const parsed = new Date(dueAt);
  if (Number.isNaN(parsed.getTime())) return null;
  return `after ${parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`;
};

const AddTask = () => {
  const history = useHistory();

  const isOnline = useOnlineStatus();
  const [entityOptions, setEntityOptions] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [reports, setReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const sync = async () => {
    if (!syncing) {
      setSyncing(true);

      setJobs(await api.syncJobs());
      setUsers(await api.syncUsers());
      setReports(await api.syncReports());

      setSyncing(false);
    }
  };

  useEffect(async () => {
    setJobs((await valueStore.getArray(`jobs`)) || []);
    setTasks((await valueStore.getArray(`tasks`)) || []);
    setUsers((await valueStore.getArray(`users`)) || []);
    setReports((await valueStore.getArray(`reports`)) || []);
  }, []);

  usePageTitle(`Add Task`);

  const formikProps = {
    initialValues: {
      assignee_id: '',
      due_at: '',
      name: '',
      description: '',
      entity_type: null,
      entity_id: null,
      is_recurring: false,
      recurrence_trigger: 'schedule',
      recurrence_interval_days: '',
      schedule_anchor: 'today',
      due_at_offset_days: '',
    },
  };

  const validationSchema = Yup.object().shape({
    assignee_id: Yup.string().required('Please select an Assignee'),
    name: Yup.string().required('Please enter a name'),
    description: Yup.string().required('Please enter a description'),

    recurrence_trigger: Yup.mixed().when('is_recurring', {
      is: true,
      then: Yup.string().oneOf(['schedule', 'on_close']).required('Please choose a trigger'),
    }),

    recurrence_interval_days: Yup.mixed().when(['is_recurring', 'recurrence_trigger'], {
      is: (is_recurring, recurrence_trigger) => is_recurring && recurrence_trigger === 'schedule',
      then: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .integer('Days must be a whole number')
        .moreThan(0, 'Please set a valid number of days')
        .required('Please set the interval'),
    }),

    due_at_offset_days: Yup.mixed().when('is_recurring', {
      is: true,
      then: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .integer('Days must be a whole number')
        .moreThan(0, 'Please set a valid number of days')
        .nullable(),
    }),
  });

  const onSubmit = useFormikSubmit(async (values) => {
    const { organization_id } = useOrganization();
    setSubmitting(true);
    tasks.push({
      ...values,
      temp_id: uuidv4(),
      organization_id: organization_id,
      created_at: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
      new: true,
    });

    setTasks(tasks);

    await valueStore.set(`tasks`, tasks);

    history.push(route('tasks'));
  });

  useEffect(async () => {
    if (isOnline) {
      await sync();
    }
  }, [JSON.stringify(tasks)]);

  return (
    <div className="m-4">
      <SyncSpinner loading={submitting} />

      <SecondaryButton as={Link} to={route('tasks')} className="mb-12 w-full py-2">
        <Icon path={mdiArrowLeft} size={1} className="mr-1" /> Cancel
      </SecondaryButton>

      <Formik {...formikProps} onSubmit={onSubmit} validationSchema={validationSchema}>
        {({ values: { entity_type, is_recurring, due_at, recurrence_trigger }, errors }) => {
          useEffect(() => {
            if (entity_type === entityTypeOptions[1].value) {
              setEntityOptions(jobs);
            }
            if (entity_type === entityTypeOptions[2].value) {
              setEntityOptions(reports);
            }
          }, [entity_type]);

          const anchorLabel = formatAnchorLabel(due_at);

          return (
            <Form>
              <FormWrapper className="rounded-lg bg-white shadow-md">
                <FieldWrapper>
                  <SelectField name="assignee_id" label="Assignee" options={users} valueKey="id" labelKey="name" isRequired={true} />
                </FieldWrapper>
                <FieldWrapper>
                  <SelectField name="entity_type" label="Link to" options={entityTypeOptions} />
                  <FieldTransition show={entityOptions.length}>
                    <div className="mt-4">
                      <SelectField name="entity_id" label="Job" options={entityOptions} valueKey="id" labelKey="name" />
                    </div>
                  </FieldTransition>
                </FieldWrapper>

                <FieldWrapper>
                  <TextField name="due_at" type="datetime-local" />
                </FieldWrapper>

                <FieldWrapper>
                  <TextField name="name" isRequired={true} />
                </FieldWrapper>

                <FieldWrapper>
                  <TextareaField name="description" isRequired={true} className="min-h-42" />
                </FieldWrapper>

                <FieldWrapper>
                  <SwitchField name="is_recurring" label="Repeat this task" yesText="Yes" noText="No" />
                </FieldWrapper>

                {is_recurring && (
                  <>
                    <FieldWrapper>
                      <p className="mb-2 text-sm text-gray-800">Create the next task</p>
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <Field type="radio" name="recurrence_trigger" value="schedule" className="form-radio" />
                          <span className="ml-2">On a schedule</span>
                        </label>
                        <label className="flex items-center">
                          <Field type="radio" name="recurrence_trigger" value="on_close" className="form-radio" />
                          <span className="ml-2">When this task is closed</span>
                        </label>
                      </div>
                    </FieldWrapper>

                    {recurrence_trigger === 'schedule' && (
                      <>
                        <FieldWrapper>
                          <p className="flex items-center text-sm text-gray-800">
                            <span className="mr-3 w-10">Every</span>
                            <span className="w-24">
                              <TextField name="recurrence_interval_days" type="number" label={false} />
                            </span>
                            <span className="ml-3">
                              days <span className="text-red-500">*</span>
                            </span>
                          </p>
                        </FieldWrapper>

                        {anchorLabel && (
                          <FieldWrapper>
                            <p className="mb-2 text-sm text-gray-800">Starting from</p>
                            <div className="space-y-2">
                              <label className="flex items-center">
                                <Field type="radio" name="schedule_anchor" value="today" className="form-radio" />
                                <span className="ml-2">today</span>
                              </label>
                              <label className="flex items-center">
                                <Field type="radio" name="schedule_anchor" value="after_due_at" className="form-radio" />
                                <span className="ml-2">{anchorLabel}</span>
                              </label>
                            </div>
                          </FieldWrapper>
                        )}
                      </>
                    )}

                    <FieldWrapper>
                      <p className="flex items-center text-sm text-gray-800">
                        <span className="mr-3 w-10">Due</span>
                        <span className="w-24">
                          <TextField name="due_at_offset_days" type="number" label={false} />
                        </span>
                        <span className="ml-3">days after creation</span>
                      </p>
                    </FieldWrapper>
                  </>
                )}

                <FieldWrapper className="mt-6 grid grid-cols-2 gap-4">
                  <div />
                  <PrimaryButton type="submit" className="w-full" disabled={Object.values(errors).length}>
                    Submit <Icon path={mdiChevronRight} size={1} />
                  </PrimaryButton>
                </FieldWrapper>
              </FormWrapper>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default AddTask;
